/**
 * @fileoverview Service for fetching chatbot configuration from the backend API
 * 
 * This service handles all API calls related to chatbot configuration,
 * following the React architecture guidelines for service layer separation.
 * 
 * @module services/chatbotService
 * @version 1.0.0
 */

import { HttpRequest } from '../utils/httpClient';

import { createLogger } from '../utils/logger';
const logger = createLogger('services');

/**
 * Chatbot configuration structure
 */
export interface ChatbotConfiguration {
    botName: string;
    chatbotBgMessageColor: string;
    chatbotTextMessageColor: string;
    colorBg: string;
    colorMain: string;
    colorTitle: string;
    firstMessage: string;
    greeting: string;
    height: number;
    iconBgColor: string;
    chatIconSvg?: string;
    iconIsCircle: string;
    marginX: string;
    marginY: string;
    pos: string;
    showUnread: string;
    userBgMessageColor: string;
    userTextMessageColor: string;
    width: number;
    wsUrl: string;
    voiceLanguage?: string;
}

/**
 * Raw response from backend API
 */
interface ChatbotApiResponse {
    name?: string;
    webchatConfig: {
        botName?: string;
        name?: string;
        colorMain?: string;
        colorTitle?: string;
        greeting?: string;
        iconBgColor?: string;
        chatIconSvg?: string;
        voiceLanguage?: string;
        wsUrl?: string;
    };
}

/**
 * Message payload for sending messages
 */
export interface SendMessagePayload {
    message: string;
    chatbotId: string;
    channelId: string;
    correlationId?: string;
    from?: string;
    userName?: string;
    product?: string;
    country?: string;
    email?: string;
    phoneNumber?: string;
}

/**
 * Response from sendMessage API
 */
export interface SendMessageResponse {
    message: string;
    correlationId: string;
    conversationId?: string;
    channelId: string;
}

/**
 * Service for fetching chatbot configuration
 */
class ChatbotService {
    private httpClient: HttpRequest;
    private fallbackHttpClient?: HttpRequest;

    constructor(baseUrl: string, fallbackUrl?: string) {
        this.httpClient = new HttpRequest(baseUrl);
        if (fallbackUrl) {
            this.fallbackHttpClient = new HttpRequest(fallbackUrl);
        }
    }

    /**
     * Fetch chatbot configuration from the API
     * 
     * @param chatbotId - The chatbot ID to fetch configuration for
     * @param useFallback - Whether to use fallback URL if primary fails
     * @returns Chatbot configuration object
     * @throws Error if configuration cannot be fetched
     */
    async getChatbotConfig(chatbotId: string, useFallback: boolean = false): Promise<ChatbotConfiguration> {
        try {
            const response = await this.httpClient.get(chatbotId);
            
            if (response.status === 200) {
                return this.transformApiResponse(response.data);
            }
            
            throw new Error(`Failed to fetch chatbot config: ${response.status}`);
        } catch (error) {
            // Try fallback if available and requested
            if (useFallback && this.fallbackHttpClient) {
                logger.warn('Primary chatbot config fetch failed, trying fallback...', error);
                try {
                    const fallbackResponse = await this.fallbackHttpClient.get(chatbotId);
                    if (fallbackResponse.status === 200) {
                        logger.info('Successfully fetched chatbot config from fallback');
                        return this.transformApiResponse(fallbackResponse.data);
                    }
                } catch (fallbackError) {
                    logger.error('Fallback chatbot config fetch also failed:', fallbackError);
                }
            }
            
            throw error;
        }
    }

    /**
     * Send a message via HTTP API
     * 
     * @param payload - Message payload to send
     * @returns Response from the API
     * @throws Error if message cannot be sent
     */
    async sendMessage(payload: SendMessagePayload): Promise<SendMessageResponse> {
        try {
            const response = await this.httpClient.post(payload, 'message');
            
            if (response.status === 200) {
                return response.data;
            }
            
            throw new Error(`Failed to send message: ${response.status}`);
        } catch (error) {
            // Try fallback if available
            if (this.fallbackHttpClient) {
                logger.warn('Primary sendMessage failed, trying fallback...', error);
                try {
                    const fallbackResponse = await this.fallbackHttpClient.post(payload, 'message');
                    if (fallbackResponse.status === 200) {
                        logger.info('Successfully sent message via fallback');
                        return fallbackResponse.data;
                    }
                } catch (fallbackError) {
                    logger.error('Fallback sendMessage also failed:', fallbackError);
                }
            }
            
            throw error;
        }
    }

    /**
     * Transform API response to ChatbotConfiguration format
     * 
     * @param data - Raw API response data
     * @returns Transformed configuration object
     */
    private transformApiResponse(data: ChatbotApiResponse): ChatbotConfiguration {
        const webchatConfig = data.webchatConfig || {};
        
        return {
            botName: data.name || webchatConfig.botName || webchatConfig.name || "Assistant",
            chatbotBgMessageColor: "#f0f2f5",
            chatbotTextMessageColor: "#000000",
            colorBg: "#ffffff",
            colorMain: webchatConfig.colorMain || '#7E70B3',
            colorTitle: webchatConfig.colorTitle || '#E3F571',
            firstMessage: "Hello! How can I help you?",
            greeting: webchatConfig.greeting || '',
            height: 500,
            iconBgColor: webchatConfig.iconBgColor || "#ffffff",
            chatIconSvg: webchatConfig.chatIconSvg,
            iconIsCircle: "true",
            marginX: "20px",
            marginY: "20px",
            pos: "bottom-right",
            showUnread: "true",
            userBgMessageColor: "#f0f2f5",
            userTextMessageColor: "#000000",
            width: 470,
            wsUrl: webchatConfig.wsUrl || '',
            voiceLanguage: webchatConfig.voiceLanguage || 'off'
        };
    }
}

/**
 * Create a chatbot service instance
 * 
 * @param baseUrl - Base URL for the chatbot API
 * @param fallbackUrl - Optional fallback URL for on-premise scenarios
 * @returns ChatbotService instance
 */
export function createChatbotService(baseUrl: string, fallbackUrl?: string): ChatbotService {
    return new ChatbotService(baseUrl, fallbackUrl);
}

